import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  MenuItem,
  Select,
  SelectChangeEvent,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { brand, gray } from "../../theme/themePrimitives";
import { EllipsisTooltip } from "../EllipsisTooltip/EllipsisTooltip";

export type SortOrder = "asc" | "desc";

const DOTS = "dots";

/** Reserved width for the expand/collapse control so first-column text aligns across rows. */
const EXPAND_COLUMN_WIDTH = 36;
const CHECKBOX_CELL_WIDTH = 52;

const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, idx) => start + idx);

const buildPaginationRange = (
  page: number,
  totalPages: number,
  siblingCount = 1,
  boundaryCount = 1,
) => {
  const totalNumbers = siblingCount * 2 + boundaryCount * 2 + 3;

  if (totalNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const startPages = range(1, boundaryCount);
  const endPages = range(totalPages - boundaryCount + 1, totalPages);

  const siblingsStart = Math.max(
    Math.min(
      page - siblingCount,
      totalPages - boundaryCount - siblingCount * 2 - 1,
    ),
    boundaryCount + 2,
  );

  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    totalPages - boundaryCount - 1,
  );

  const pages: (number | typeof DOTS)[] = [...startPages];

  if (siblingsStart > boundaryCount + 2) {
    pages.push(DOTS);
  } else if (boundaryCount + 1 < totalPages - boundaryCount) {
    pages.push(boundaryCount + 1);
  }

  pages.push(...range(siblingsStart, siblingsEnd));

  if (siblingsEnd < totalPages - boundaryCount - 1) {
    pages.push(DOTS);
  } else if (totalPages - boundaryCount > boundaryCount) {
    pages.push(totalPages - boundaryCount);
  }

  pages.push(...endPages);

  return pages;
};

export type ColumnDef<T> = {
  id: string;
  label: string | ReactNode;
  sortable?: boolean;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  /**
   * Content renderer. When omitted, the value of row[id] is shown.
   */
  render?: (row: T) => ReactNode;
  /**
   * Value extractor used for the default renderer and sorting.
   */
  getValue?: (row: T) => string | number | null | undefined;
  sticky?: boolean;
};

export type StandardTableProps<T> = {
  /**
   * Data to display in the table. This should be the result from API calls.
   * DataTable does NOT filter this data - all filtering should be done server-side.
   */
  data: T[];
  columns: ColumnDef<T>[];
  /**
   * Total count of all records (not just the current page).
   * Used for pagination calculations.
   */
  totalCount: number;
  /**
   * 1-based page index.
   */
  page: number;
  rowsPerPage: number;
  rowsPerPageOptionsOverride?: number[];
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rows: number) => void;
  sort?: { orderBy: string; order: SortOrder };
  onSortChange?: (orderBy: string, order: SortOrder) => void;
  alwaysShowSortArrows?: boolean;
  firstClickOrder?: SortOrder;
  /**
   * Search query value. This is passed to the parent component via onSearchChange
   * for server-side filtering. DataTable does NOT perform client-side filtering.
   * The parent component should handle API calls with the search query.
   */
  search?: string;
  /**
   * Callback when search value changes (after debounce).
   * Parent component should use this to make API calls with the search query.
   */
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  actions?: ReactNode;
  title?: ReactNode;
  isLoading?: boolean;
  stickyHeader?: boolean;
  renderEmptyState?: ReactNode;
  getRowId?: (row: T, index: number) => string | number;
  /**
   * Optional nested rows. When provided, an expand column is auto-rendered.
   * getRowId is required to keep expansion state stable.
   */
  getNestedRows?: (row: T) => T[] | undefined;
  nestedRowIndent?: number;
  /**
   * Enable row selection with checkboxes.
   */
  enableSelection?: boolean;
  /**
   * Array of selected rows. Required if enableSelection is true.
   */
  selectedRows?: T[];
  /**
   * Callback when selection changes. Required if enableSelection is true.
   */
  onSelectionChange?: (selected: T[]) => void;
  /**
   * Optional function to determine if a row is selected.
   * If not provided, will use selectedRows array with getRowId for comparison.
   */
  isRowSelected?: (row: T) => boolean;
  /**
   * Label for selected items count (e.g., "Selected users", "Selected templates").
   * If provided, automatically generates a title with count badge when items are selected.
   */
  selectedCountTitle?: string;
  /**
   * Label for clear all button (e.g., "Clear all").
   * If provided along with onClearAll, shows a "Clear all" button in actions.
   */
  clearAllLabel?: string;
  /**
   * Callback for clear all button. If provided along with clearAllLabel,
   * shows a "Clear all" button in actions.
   */
  onClearAll?: () => void;
  /**
   * Optional content to render between the header section and the table.
   * Useful for filter chips, additional filters, etc.
   */
  headerContent?: ReactNode;
  /**
   * Optional function to get custom sx styles for a row.
   * Useful for applying row-level styling like borders, backgrounds, etc.
   */
  getRowSx?: (row: T, index: number) => Record<string, any>;
  /**
   * Callback when a row is clicked. When provided, rows show pointer cursor and hover highlight.
   */
  onRowClick?: (row: T) => void;
  /**
   * Optional function to get custom sx styles for a cell.
   * Useful for applying cell-level styling like borders, backgrounds, etc.
   * Parameters: (row, column, colIndex, depth) - depth is 0 for top-level rows, 1+ for nested.
   */
  getCellSx?: (
    row: T,
    column: ColumnDef<T>,
    colIndex: number,
    depth?: number,
  ) => Record<string, any>;
  /**
   * Enable drag and drop reordering of rows.
   */
  enableDragAndDrop?: boolean;
  /**
   * Callback when drag ends. Receives the drag event with active and over IDs.
   * The parent should handle reordering the data array.
   */
  onDragEnd?: (event: DragEndEvent) => void;
  /**
   * Custom icon to render in the drag handle cell. Defaults to DragIndicatorIcon.
   */
  dragHandleIcon?: ReactNode;
  /**
   * When true, renders without the Paper wrapper so the parent can control the card surface.
   */
  disablePaper?: boolean;
  /**
   * When true, hides the table header row.
   */
  hideHeader?: boolean;
  tableMaxHeight?: number | string;
};

/**
 * A compact, reusable table wrapper that standardizes search (with debounce),
 * sorting, and 10-step pagination while keeping the existing UI look-and-feel.
 */
export function DataTable<T>({
  data,
  columns,
  totalCount,
  page,
  rowsPerPage,
  rowsPerPageOptionsOverride,
  onPageChange,
  onRowsPerPageChange,
  sort,
  onSortChange,
  alwaysShowSortArrows = false,
  firstClickOrder = "asc",
  search = "",
  onSearchChange,
  searchPlaceholder = "Search",
  actions,
  title,
  isLoading = false,
  stickyHeader = true,
  renderEmptyState,
  getRowId,
  getNestedRows,
  nestedRowIndent = 5,
  enableSelection = false,
  selectedRows = [],
  onSelectionChange,
  isRowSelected,
  selectedCountTitle,
  clearAllLabel,
  onClearAll,
  headerContent,
  getRowSx,
  getCellSx,
  enableDragAndDrop = false,
  onDragEnd,
  dragHandleIcon,
  disablePaper = false,
  hideHeader = false,
  onRowClick,
  tableMaxHeight,
}: StandardTableProps<T>) {
  const [localSearch, setLocalSearch] = useState(search);
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const sensors = useSensors(useSensor(PointerSensor));
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startScrollLeft: number; trackW: number; thumbW: number } | null>(null);
  const [scrollbar, setScrollbar] = useState({ show: false, thumbWidth: 0, thumbLeft: 0 });
  const thumbRef = useRef<HTMLDivElement>(null);
  const SCROLL_ARROW_W = 44;

  const restrictToTableBody: Modifier = useCallback(
    ({ transform, draggingNodeRect }) => {
      const base = { ...transform, x: 0 };
      if (!draggingNodeRect || !tableBodyRef.current) return base;
      const bodyRect = tableBodyRef.current.getBoundingClientRect();
      const minY = bodyRect.top - draggingNodeRect.top;
      const maxY = bodyRect.bottom - draggingNodeRect.bottom;
      return { ...base, y: Math.min(Math.max(base.y, minY), maxY) };
    },
    [],
  );

  const refreshScrollbar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || dragRef.current) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    if (!scrollable) { setScrollbar(s => ({ ...s, show: false })); return; }
    const trackW = el.clientWidth - SCROLL_ARROW_W * 2;
    const thumbW = Math.max((el.clientWidth / el.scrollWidth) * trackW, 44);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbLeft = maxScroll > 0 ? (el.scrollLeft / maxScroll) * (trackW - thumbW) : 0;
    setScrollbar({ show: true, thumbWidth: thumbW, thumbLeft });
  }, [SCROLL_ARROW_W]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshScrollbar, { passive: true });
    const ro = new ResizeObserver(refreshScrollbar);
    ro.observe(el);
    refreshScrollbar();
    return () => { el.removeEventListener("scroll", refreshScrollbar); ro.disconnect(); };
  }, [refreshScrollbar]);

  useEffect(() => {
    const move = (clientX: number) => {
      const d = dragRef.current;
      if (!d) return;
      const el = tableScrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const scale = maxScroll / Math.max(d.trackW - d.thumbW, 1);
      const newScrollLeft = Math.max(0, Math.min(maxScroll, d.startScrollLeft + (clientX - d.startX) * scale));
      el.scrollLeft = newScrollLeft;
      // Directly update DOM — no React state, no re-render, pixel-smooth
      if (thumbRef.current) {
        const newLeft = maxScroll > 0 ? (newScrollLeft / maxScroll) * (d.trackW - d.thumbW) : 0;
        thumbRef.current.style.left = `${newLeft}px`;
      }
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      refreshScrollbar(); // sync React state after drag ends
    };
    const onMM = (e: MouseEvent) => move(e.clientX);
    const onTM = (e: TouchEvent) => {
      if (!dragRef.current) return; // no active thumb drag — let native touch scroll happen
      e.preventDefault();
      move(e.touches[0].clientX);
    };
    document.addEventListener("mousemove", onMM);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", end);
    return () => {
      document.removeEventListener("mousemove", onMM);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onTM);
      document.removeEventListener("touchend", end);
    };
  }, [refreshScrollbar]);

  const onThumbDragStart = (clientX: number) => {
    const el = tableScrollRef.current;
    if (!el) return;
    dragRef.current = { startX: clientX, startScrollLeft: el.scrollLeft, trackW: el.clientWidth - SCROLL_ARROW_W * 2, thumbW: scrollbar.thumbWidth };
  };

  const scrollByAmount = (amount: number) => {
    tableScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  const SEARCH_DEBOUNCE = 400;

  // Use ref for onSearchChange to avoid triggering effect on callback reference changes
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Keep local search in sync with controlled prop
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  // Debounced search propagation
  // NOTE: DataTable does NOT filter data client-side.
  // The search value is passed to the parent component via onSearchChange,
  // and the parent should make API calls with the search query.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (onSearchChangeRef.current) {
        onSearchChangeRef.current(localSearch.trim());
      }
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(handle);
  }, [localSearch]);

  const rowsPerPageOptions = useMemo(() => {
    if (rowsPerPageOptionsOverride) {
      const withoutTotal = rowsPerPageOptionsOverride.filter(
        (o) => o !== totalCount,
      );
      return [...withoutTotal, totalCount];
    }

    // Standardized 10-step increments (10, 20, 30, ...)
    // 10 is always included as the default option
    // Only show options that are <= totalCount (don't show 20 if totalCount is 14)
    const steps: number[] = [10];

    // Generate steps in increments of 10, but only if they're <= totalCount
    for (let size = 20; size <= totalCount; size += 10) {
      steps.push(size);
    }

    // Always include the current rowsPerPage if it's not already in the list and <= totalCount
    if (
      rowsPerPage >= 10 &&
      rowsPerPage <= totalCount &&
      !steps.includes(rowsPerPage)
    ) {
      steps.push(rowsPerPage);
    }

    // Remove totalCount from steps if it exists (we'll add it at the end)
    const numericSteps = steps.filter((step) => step !== totalCount);
    // Sort numeric steps
    const sortedSteps = Array.from(new Set(numericSteps)).sort((a, b) => a - b);

    // Always include "All" option (represented as totalCount) at the end
    return [...sortedSteps, totalCount];
  }, [rowsPerPage, totalCount, rowsPerPageOptionsOverride]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const paginationRange = useMemo(
    () => buildPaginationRange(page, totalPages, 1, 1),
    [page, totalPages],
  );

  // If the dataset shrinks or the current page was computed incorrectly, avoid
  // staying on an out-of-range page (empty table while totalCount > 0 elsewhere).
  const onPageChangeRef = useRef(onPageChange);
  useEffect(() => {
    onPageChangeRef.current = onPageChange;
  }, [onPageChange]);
  useEffect(() => {
    if (page > totalPages) {
      onPageChangeRef.current?.(totalPages);
    }
  }, [page, totalPages]);

  const handleSort = (column: ColumnDef<T>) => {
    if (!column.sortable || !onSortChange) return;
    const isSameColumn = sort?.orderBy === column.id;
    const nextOrder: SortOrder = isSameColumn
      ? sort?.order === "asc"
        ? "desc"
        : "asc"
      : firstClickOrder;
    onSortChange(column.id, nextOrder);
  };

  // Selection helpers
  const getRowIdValue = (row: T, index: number): string | number => {
    return getRowId ? getRowId(row, index) : index;
  };

  const getRowIdForComparison = (row: T): string | number => {
    if (getRowId) {
      // For selected rows, we don't have the original index, so we use 0 as a placeholder
      // The getRowId function should work with index 0 or any index
      return getRowId(row, 0);
    }
    // Fallback: try to find the row in data to get its index
    const foundIndex = data.findIndex((r) => r === row);
    return foundIndex >= 0 ? foundIndex : JSON.stringify(row);
  };

  const checkIsRowSelected = (row: T, index: number): boolean => {
    if (isRowSelected) {
      return isRowSelected(row);
    }
    if (!enableSelection || selectedRows.length === 0) {
      return false;
    }
    const rowId = getRowIdValue(row, index);
    return selectedRows.some(
      (selected) => getRowIdForComparison(selected) === rowId,
    );
  };

  const allSelected =
    enableSelection &&
    data.length > 0 &&
    data.every((row, index) => checkIsRowSelected(row, index));

  const someSelected =
    enableSelection &&
    data.some((row, index) => checkIsRowSelected(row, index)) &&
    !allSelected;

  const handleRowToggle = (row: T, index: number) => {
    if (!enableSelection || !onSelectionChange) return;

    const isSelected = checkIsRowSelected(row, index);
    if (isSelected) {
      const rowId = getRowIdValue(row, index);
      onSelectionChange(
        selectedRows.filter(
          (selected) => getRowIdForComparison(selected) !== rowId,
        ),
      );
    } else {
      onSelectionChange([...selectedRows, row]);
    }
  };

  const handleSelectAll = () => {
    if (!enableSelection || !onSelectionChange) return;

    const currentPageIds = new Set(
      data.map((row, index) => getRowIdValue(row, index)),
    );

    if (allSelected) {
      onSelectionChange(
        selectedRows.filter(
          (selected) => !currentPageIds.has(getRowIdForComparison(selected)),
        ),
      );
      return;
    }

    const merged = new Map(
      selectedRows.map((selected) => [
        getRowIdForComparison(selected),
        selected,
      ]),
    );

    data.forEach((row, index) => {
      const rowId = getRowIdValue(row, index);
      if (!merged.has(rowId)) {
        merged.set(rowId, row);
      }
    });

    onSelectionChange(Array.from(merged.values()));
  };

  const renderCell = (row: T, column: ColumnDef<T>) => {
    if (column.render) return column.render(row);
    if (column.getValue) return column.getValue(row);
    // @ts-ignore - allow indexed access as a fallback
    return (row as any)?.[column.id] ?? "-";
  };

  const toggleExpand = (rowId: string | number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const SortableRow = ({
    row,
    index,
    depth,
    parentBgColor,
  }: {
    row: T;
    index: number;
    depth: number;
    parentBgColor?: string;
  }) => {
    const rowId = getRowId ? getRowId(row, index) : index;
    const nested = getNestedRows?.(row) ?? [];
    const isExpandable = nested.length > 0;
    const isExpanded = expanded.has(rowId);
    const isSelected = checkIsRowSelected(row, index);

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: rowId, disabled: depth > 0 });

    const baseBgColor =
      depth === 0
        ? index % 2 !== 0
          ? "#ffffff"
          : gray[50]
        : parentBgColor || "#ffffff";

    const nestedBgColor = isExpandable && isExpanded ? baseBgColor : undefined;
    const bgColor = nestedBgColor !== undefined ? nestedBgColor : baseBgColor;

    const customRowSx = getRowSx ? getRowSx(row, index) : {};
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      backgroundColor: isDragging ? "#F0F0F0" : bgColor,
    };

    const rowCells = (
      <TableRow
        ref={setNodeRef}
        style={style}
        {...attributes}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        sx={{
          position: "relative",
          ...(onRowClick
            ? {
                cursor: "pointer",
                "&:hover": { backgroundColor: `${gray[100]} !important` },
              }
            : {}),
          ...customRowSx,
        }}
      >
        {enableDragAndDrop && depth === 0 && (
          <TableCell
            {...listeners}
            sx={{
              py: 0.75,
              borderColor: "#EAECF0",
              cursor: isDragging ? "grabbing" : "grab",
              width: 48,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {dragHandleIcon ?? (
                <MenuIcon fontSize="small" sx={{ color: gray[600] }} />
              )}
            </Box>
          </TableCell>
        )}
        {enableSelection && (
          <TableCell
            padding="checkbox"
            sx={{ position: "sticky", left: 0, zIndex: 2, backgroundColor: bgColor }}
          >
            <Checkbox
              checked={isSelected}
              onChange={() => handleRowToggle(row, index)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
        )}

        {columns.map((column, colIndex) => {
          const customCellSx = getCellSx
            ? getCellSx(row, column, colIndex, depth)
            : {};
          const cell = renderCell(row, column);

          return (
            <TableCell
              key={column.id}
              align={column.align}
              sx={{
                py: 0.75,
                borderColor: "#EAECF0",
                maxWidth: column.maxWidth ?? 240,
                ...(depth > 0 && colIndex === 0
                  ? { pl: (depth + 1) * nestedRowIndent }
                  : {}),
                ...(column.sticky
                  ? { position: "sticky", left: enableSelection ? CHECKBOX_CELL_WIDTH : 0, zIndex: 1, backgroundColor: bgColor }
                  : {}),
                ...customCellSx,
              }}
            >
              {colIndex === 0 && getNestedRows ? (
                <Box
                  sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: EXPAND_COLUMN_WIDTH,
                      minWidth: EXPAND_COLUMN_WIDTH,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    {isExpandable ? (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(rowId)}
                        sx={{
                          border: "none",
                          p: 0.5,
                          margin: 0,
                          backgroundColor: "transparent",
                          "&.MuiIconButton-root": {
                            width: 28,
                            minWidth: 28,
                            height: 28,
                          },
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    ) : null}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EllipsisTooltip
                      title={cell}
                      maxWidth={column.maxWidth ?? 240}
                    >
                      {cell}
                    </EllipsisTooltip>
                  </Box>
                </Box>
              ) : (
                <EllipsisTooltip title={cell} maxWidth={column.maxWidth ?? 240}>
                  {cell}
                </EllipsisTooltip>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );

    if (!isExpandable || !isExpanded) {
      return rowCells;
    }

    return (
      <React.Fragment key={rowId}>
        {rowCells}
        {renderRows(nested, depth + 1, nestedBgColor)}
      </React.Fragment>
    );
  };

  const renderRows = (rows: T[], depth = 0, parentBgColor?: string) =>
    rows.map((row, index) => {
      const rowId = getRowId ? getRowId(row, index) : index;
      const nested = getNestedRows?.(row) ?? [];
      const isExpandable = nested.length > 0;
      const isExpanded = expanded.has(rowId);
      const isSelected = checkIsRowSelected(row, index);

      const baseBgColor =
        depth === 0
          ? index % 2 !== 0
            ? "#ffffff"
            : gray[50]
          : parentBgColor || "#ffffff";

      const nestedBgColor =
        isExpandable && isExpanded ? baseBgColor : undefined;
      const bgColor = nestedBgColor !== undefined ? nestedBgColor : baseBgColor;

      const customRowSx = getRowSx ? getRowSx(row, index) : {};
      const rowCells = (
        <TableRow
          key={rowId}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          sx={{
            backgroundColor: bgColor,
            position: "relative",
            ...(onRowClick
              ? {
                  cursor: "pointer",
                  "&:hover": { backgroundColor: `${gray[100]} !important` },
                }
              : {}),
            ...customRowSx,
          }}
        >
          {enableSelection && (
            <TableCell
              padding="checkbox"
              sx={{ position: "sticky", left: 0, zIndex: 2, backgroundColor: bgColor }}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => handleRowToggle(row, index)}
                onClick={(e) => e.stopPropagation()}
              />
            </TableCell>
          )}

          {columns.map((column, colIndex) => {
            const customCellSx = getCellSx
              ? getCellSx(row, column, colIndex, depth)
              : {};
            const cell = renderCell(row, column);

            return (
              <TableCell
                key={column.id}
                align={column.align}
                sx={{
                  py: 0.75,
                  borderColor: "#EAECF0",
                  maxWidth: column.maxWidth ?? 240,
                  ...(depth > 0 && colIndex === 0
                    ? { pl: (depth + 1) * nestedRowIndent }
                    : {}),
                  ...(column.sticky
                    ? { position: "sticky", left: enableSelection ? CHECKBOX_CELL_WIDTH : 0, zIndex: 1, backgroundColor: bgColor }
                    : {}),
                  ...customCellSx,
                }}
              >
                {colIndex === 0 && getNestedRows ? (
                  <Box
                    sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: EXPAND_COLUMN_WIDTH,
                        minWidth: EXPAND_COLUMN_WIDTH,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      {isExpandable ? (
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(rowId)}
                          sx={{
                            border: "none",
                            p: 0.5,
                            margin: 0,
                            backgroundColor: "transparent",
                            "&.MuiIconButton-root": {
                              width: 28,
                              minWidth: 28,
                              height: 28,
                            },
                          }}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      ) : null}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EllipsisTooltip
                        title={cell}
                        maxWidth={column.maxWidth ?? 240}
                      >
                        {cell}
                      </EllipsisTooltip>
                    </Box>
                  </Box>
                ) : (
                  <EllipsisTooltip
                    title={cell}
                    maxWidth={column.maxWidth ?? 240}
                  >
                    {cell}
                  </EllipsisTooltip>
                )}
              </TableCell>
            );
          })}
        </TableRow>
      );

      if (!isExpandable || !isExpanded) return rowCells;

      return (
        <React.Fragment key={rowId}>
          {rowCells}
          {renderRows(nested, depth + 1, nestedBgColor)}
        </React.Fragment>
      );
    });

  const totalColumns =
    columns.length + (enableSelection ? 1 : 0) + (enableDragAndDrop ? 1 : 0);

  const sortableItems = enableDragAndDrop
    ? data.map((row, index) => getRowIdValue(row, index))
    : [];

  const tableContent = (
    <Table
      stickyHeader={stickyHeader}
      size="small"
      sx={{ tableLayout: "auto", width: "100%" }}
    >
      {!hideHeader && (
        <TableHead>
          <TableRow>
            {enableDragAndDrop && (
              <TableCell
                sx={{
                  borderTop: "1px solid",
                  borderColor: "#EAECF0",
                  backgroundColor: "white",
                  width: 48,
                }}
              />
            )}
            {enableSelection && (
              <TableCell
                padding="checkbox"
                sx={{
                  borderTop: "1px solid",
                  borderColor: "#EAECF0",
                  backgroundColor: "white",
                  position: "sticky",
                  left: 0,
                  zIndex: 5,
                }}
              >
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                sx={{
                  borderTop: onSearchChange ? "1px solid" : "none",
                  borderColor: "#EAECF0",
                  backgroundColor: "white",
                  minWidth: column.minWidth,
                  maxWidth: column.maxWidth ?? 240,
                  width: column.width,
                  ...(column.sticky
                    ? { position: "sticky", left: enableSelection ? CHECKBOX_CELL_WIDTH : 0, zIndex: 4 }
                    : {}),
                }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={alwaysShowSortArrows || sort?.orderBy === column.id}
                    direction={
                      sort?.orderBy === column.id ? sort?.order : "asc"
                    }
                    onClick={() => handleSort(column)}
                    sx={{
                      "&.MuiTableSortLabel-root": {
                        padding: 0,
                        color: "#475467",
                        fontWeight: 500,
                        fontSize: 12,
                      },
                      "& .MuiTableSortLabel-icon": {
                        marginLeft: "4px",
                        color: "#475467",
                      },
                      "&:hover": { textDecoration: "none" },
                      "&.Mui-focusVisible": { outline: "none" },
                    }}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  <Typography
                    component="span"
                    sx={{
                      color: "#475467",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {column.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
      )}

      <TableBody ref={tableBodyRef}>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
              <CircularProgress size={64} />
            </TableCell>
          </TableRow>
        ) : data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
              {renderEmptyState ?? (
                <Typography variant="body2" color="text.secondary">
                  No records found.
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ) : enableDragAndDrop ? (
          data.map((row, index) => (
            <SortableRow
              key={getRowIdValue(row, index)}
              row={row}
              index={index}
              depth={0}
            />
          ))
        ) : (
          // NOTE: data prop is used directly without any filtering.
          // All filtering should be done server-side via API calls.
          renderRows(data)
        )}
      </TableBody>
    </Table>
  );

  const paginationContent = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderTop: "1px solid #EAECF0",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Select
          size="small"
          value={
            rowsPerPageOptions.includes(rowsPerPage)
              ? rowsPerPage
              : rowsPerPageOptions[0]
          }
          onChange={(e: SelectChangeEvent<number>) => {
            const nextRows = Number(e.target.value);
            onRowsPerPageChange?.(nextRows);
          }}
          renderValue={(value) =>
            value === totalCount ? "All" : String(value)
          }
        >
          {rowsPerPageOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt === totalCount ? "All" : opt}
            </MenuItem>
          ))}
        </Select>
        <Typography>Show rows</Typography>
      </Stack>

      <Stack direction="row" alignItems="center">
        <IconButton
          size="small"
          onClick={() => onPageChange?.(Math.max(1, page - 1))}
          disabled={page <= 1}
          sx={{ borderRadius: "8px 0 0 8px", width: "auto", px: 2 }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <ArrowBackIcon fontSize="small" sx={{ color: gray[800] }} />
            <Typography lineHeight="normal">Previous</Typography>
          </Box>
        </IconButton>

        {paginationRange.map((item, idx) =>
          item === DOTS ? (
            <Button
              key={`dots-${idx}`}
              size="small"
              variant={"outlined"}
              sx={{
                minWidth: 36,
                borderRadius: 0,
                color: gray[800],
                borderWidth: "1px 1px 1px 1px",
                borderColor: gray[300],
                backgroundColor: "white",
                "&:hover": {
                  backgroundColor: "white",
                  borderColor: gray[300],
                },
              }}
            >
              <Typography variant="body2" sx={{ textAlign: "center" }}>
                ...
              </Typography>
            </Button>
          ) : (
            <Button
              key={`page-${item}`}
              size="small"
              variant={"outlined"}
              onClick={() => onPageChange?.(item as number)}
              sx={{
                minWidth: 36,
                borderRadius: 0,
                color: gray[800],
                borderWidth: "1px 1px 1px 1px",
                borderColor: gray[300],
                backgroundColor: item === page ? gray[200] : "white",
                "&:hover": {
                  backgroundColor:
                    item === page ? gray[200] : gray[100],
                  borderColor: gray[300],
                },
              }}
            >
              {item}
            </Button>
          ),
        )}

        <IconButton
          size="small"
          onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          sx={{ borderRadius: "0 8px 8px 0", width: "auto", px: 2 }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography lineHeight="normal">Next</Typography>
            <ArrowForwardIcon
              fontSize="small"
              sx={{ color: gray[800] }}
            />
          </Box>
        </IconButton>
      </Stack>
    </Box>
  );

  const inner = (
    <>
      {(onSearchChange || title || actions) && (
        <Box sx={{ p: 2 }}>
          {onSearchChange && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              fullWidth
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiInputBase-root": {
                  bgcolor: "#EAECF0",
                  borderRadius: "12px",
                },
              }}
            />
          )}

          {(() => {
            const hasSelected = enableSelection && selectedRows.length > 0;
            const displayTitle =
              title ||
              (selectedCountTitle && hasSelected ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6">{selectedCountTitle}</Typography>
                  <Box
                    minWidth={24}
                    height={24}
                    px={0.75}
                    borderRadius="50%"
                    bgcolor="#EFF8FF"
                    border="1px solid #B2DDFF"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography color="#175CD3" fontSize={14}>
                      {selectedRows.length}
                    </Typography>
                  </Box>
                </Box>
              ) : undefined);

            const displayActions = <>{actions}</>;

            if (!displayTitle && !displayActions) return null;

            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: onSearchChange ? 2 : 0,
                }}
              >
                {displayTitle && (
                  <Box sx={{ whiteSpace: "nowrap" }}>{displayTitle}</Box>
                )}

                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ ml: "auto" }}
                >
                  {displayActions}
                </Stack>
              </Box>
            );
          })()}
        </Box>
      )}

      {clearAllLabel && onClearAll && (
        <Box sx={{ pt: 3, px: 2 }}>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={onClearAll}
              sx={{
                borderRadius: "12px",
                backgroundColor: "#fff",
                color: brand[600],
                fontSize: "1rem",
                m: 2,
                textDecoration: "underline",
              }}
            >
              {clearAllLabel}
            </Button>
          </Box>
        </Box>
      )}

      {headerContent && <Box sx={{ px: 2 }}>{headerContent}</Box>}

      <TableContainer
        ref={tableScrollRef}
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          ...(tableMaxHeight !== undefined
            ? { maxHeight: tableMaxHeight }
            : {}),
        }}
      >
        {enableDragAndDrop ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToTableBody]}
            onDragEnd={onDragEnd || (() => {})}
          >
            <SortableContext
              items={sortableItems}
              strategy={verticalListSortingStrategy}
            >
              {tableContent}
            </SortableContext>
          </DndContext>
        ) : (
          tableContent
        )}
      </TableContainer>

      {scrollbar.show && (
        <Box sx={{ display: "flex", alignItems: "center", height: SCROLL_ARROW_W, backgroundColor: "#fff", userSelect: "none" }}>
          <Box
            sx={{ width: SCROLL_ARROW_W, height: SCROLL_ARROW_W, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            onMouseDown={() => scrollByAmount(-100)}
            onTouchStart={(e) => { e.preventDefault(); scrollByAmount(-100); }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10"><polygon points="5,1 1,5 5,9" fill="#344054"/></svg>
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: SCROLL_ARROW_W }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                height: 16,
                width: scrollbar.thumbWidth,
                left: scrollbar.thumbLeft,
                backgroundColor: "#eaecf0",
                borderRadius: "10px",
                cursor: "grab",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
              }}
              ref={thumbRef}
              onMouseDown={(e) => { e.preventDefault(); onThumbDragStart(e.clientX); }}
              onTouchStart={(e) => { e.preventDefault(); onThumbDragStart(e.touches[0].clientX); }}
            />
          </Box>
          <Box
            sx={{ width: SCROLL_ARROW_W, height: SCROLL_ARROW_W, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            onMouseDown={() => scrollByAmount(100)}
            onTouchStart={(e) => { e.preventDefault(); scrollByAmount(100); }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10"><polygon points="1,1 5,5 1,9" fill="#344054"/></svg>
          </Box>
        </Box>
      )}

      {paginationContent}
    </>
  );

  return disablePaper ? (
    inner
  ) : (
    <Paper sx={{ p: 0, borderRadius: "12px" }}>
      {inner}
    </Paper>
  );
}

export default DataTable;
